'use client';

import { useEffect } from 'react';
import { pushEvent } from '@repo/ui/src/services/analytics/gtm';

type SwitchLiveEvent = {
  id?: string;
  type?: string;
  description?: string;
  scope?: string;
  message_type?: string;
  timestamp?: number;
  url?: string;
  utm?: any;
  company_id?: string;
  product_name?: string;
  upc?: string;
  product_price?: string;
  affiliate_name?: string;
  store_id?: string;
  store_name?: string;
  store_address?: string;
  vendor_id?: string;
  [key: string]: any;
};

const EVENT_NAME_BY_ID: Record<string, string> = {
  '1': 'pl_widget_loaded',
  '4': 'pl_store_link_click',
  '7': 'pl_modal_opened',
  '9': 'pl_modal_store_click',
  '87': 'pl_store_tile_impression',
  '88': 'pl_store_tile_impression_pdp',
  '89': 'pl_buy_online_click',
  '90': 'pl_buy_online_option_impression',
};

/**
 * SwitchLiveCallback Component
 *
 * Initializes global event listeners for Locally's SwitchLive Product Locator events
 * and forwards them to Google Tag Manager.
 *
 * This component should be mounted once at the app level (e.g., in template.tsx or layout.tsx)
 * to enable automatic tracking of all Product Locator interactions across the site.
 */
export function SwitchLiveCallback() {
  function normalizeSwitchLiveForGtm(e: SwitchLiveEvent) {
    const pageLocation = e.url || (typeof window !== 'undefined' ? window.location.href : '');
    let pagePath = '';
    try {
      pagePath = pageLocation ? new URL(pageLocation).pathname : '';
    } catch {}  

    return {
      event_source: 'locally_switchlive',
      event_category: 'product_locator',

      // core event identity
      locally_id: e.id,
      locally_type: e.type,
      locally_scope: e.scope,
      locally_ts: e.timestamp,

      // page context
      page_location: pageLocation,
      page_path: pagePath,

      // product context
      locally_upc: e.upc,
      locally_product_name: e.product_name,
      locally_product_price: e.product_price,

      // commerce / retailer context
      locally_affiliate_name: e.affiliate_name,

      // store context (safe identifiers)
      locally_store_id: e.store_id,
      locally_store_name: e.store_name,
      locally_vendor_id: e.vendor_id,

      locally_desc: e.description?.slice(0, 100),
    };
  }

  useEffect(() => {
    const handleSwitchLiveEvent = (event: SwitchLiveEvent) => {
      if (event.scope !== 'PL') {
        return;
      }

      const normalizedEvent = normalizeSwitchLiveForGtm(event);
      const gtmEventName = (event.id && EVENT_NAME_BY_ID[event.id]) || 'locally_switchlive';
      pushEvent(gtmEventName, normalizedEvent);
    };

    window.switchLiveEventCallback = handleSwitchLiveEvent;

    return () => {
      // Cleanup
      delete window.switchLiveEventCallback;
    };
  }, []);

  return null;
}
